// Models for the Disbursement Document Certification module
// (ขั้นตอนการรับรองเอกสารส่งเบิก), mapped field-for-field against
// apps/api/app/models/disbursement_certification.rb (v1.2 — see
// context/business_flow/DisbursementDocumentFlow.md).
//
// v1.2 decision 8 (path split): one certification per (teacher, term, ภาค) —
// `path`/`path_label` replace the v1.1 single-cert-carries-both-curricula
// model. v1.2 decision 1 drops the whole disbursement_date/DisbursementSetting
// surface — no settings screen, no `disbursement_date` field anywhere.

// v1.2 — 7-value status_code enum (workflow `disbursement_document`), see
// DisbursementCertification::STATUS_LABELS. Unchanged from v1.1.
export type DisbursementStatusCode =
  | 'pending_teacher'
  | 'pending_head_of_dept'
  | 'pending_finance'
  | 'pending_vice_dean_admin'
  | 'pending_bundle'
  | 'delivered'
  | 'revision_schedule_requested';

export const DISBURSEMENT_STATUS_LABELS: Record<DisbursementStatusCode, string> = {
  pending_teacher: 'รออาจารย์รับรอง',
  pending_head_of_dept: 'รอหัวหน้าสาขารับรอง',
  pending_finance: 'รอเจ้าหน้าที่การเงินอนุมัติ',
  pending_vice_dean_admin: 'รอรองคณบดีฝ่ายบริหารรับรอง',
  pending_bundle: 'รอเจ้าหน้าที่สารบรรณรวมเอกสาร',
  delivered: 'ส่งเอกสารให้เจ้าหน้าที่สารบรรณแล้ว',
  revision_schedule_requested: 'ตีกลับ — ปัญหาตารางสอน',
};

// v1.2 decision 2: a cert's documents become visible on ข้อมูลเอกสารส่งเบิก once
// it reaches one of these two statuses (DisbursementCertification::COMPLETED_STATUS_CODES).
export const DISBURSEMENT_COMPLETED_STATUS_CODES: DisbursementStatusCode[] = [
  'pending_bundle',
  'delivered',
];

// v1.2 decision 8 — DisbursementCertification#path enum.
export type DisbursementPath = 'regular' | 'international';

export const DISBURSEMENT_PATH_LABELS: Record<DisbursementPath, string> = {
  regular: 'ภาคปกติ',
  international: 'ภาคนานาชาติ',
};

export interface DisbursementAcademicTerm {
  id: number;
  name: string;
  year: number;
}

export interface DisbursementDepartment {
  id: number;
  name?: string | null;
  name_th?: string | null;
  name_en?: string | null;
}

export interface DisbursementUser {
  id: number;
  full_name: string;
  email?: string | null;
}

// §4 note: hours/eligibility only — no Baht amounts until Compensation::Calculator
// ships (Phase D). Kept loose/partial since downstream fields are still shifting.
export interface DisbursementCompensationSnapshot {
  total_request_count?: number;
  approved_request_count?: number;
  pending_request_count?: number;
  total_requested_hours?: number;
  approved_hours?: number;
  pending_hours?: number;
  minimum_teaching_workload?: number;
  teaching_workload?: number;
  makeup_teaching_workload?: number;
  total_teaching_workload?: number;
  excess_hours?: number;
  academic_term?: DisbursementAcademicTerm | null;
  erp_comparison?: unknown;
}

// v1.2 §2/§3: one doc_type per generated final (doc_2/doc_5 regular,
// doc_3/doc_4 international, doc_6 cover page, doc_7 schedule) plus the
// per-cert `merged` PDF — see GeneratedPdfDocument::document_types and
// DisbursementCertification#document_summary_json (field names match the
// backend's as_json literally: `document_type`, not `doc_type`).
export type DisbursementDocType = 'doc_2' | 'doc_3' | 'doc_4' | 'doc_5' | 'doc_6' | 'doc_7' | 'merged';

export interface DisbursementDocumentEntry {
  id: number;
  // Loose `string` rather than `DisbursementDocType | string` (the latter
  // trips @typescript-eslint/no-redundant-type-constituents, since a string
  // literal union unioned with `string` collapses to `string` anyway) — the
  // backend's `latest_documents` can include "bundle" alongside the doc_2..
  // merged literals, which this field intentionally stays loose beyond.
  document_type: string;
  generated_at?: string | null;
  byte_size?: number | null;
}

// Thai labels for the two-section layout (§4/§5): "เอกสารส่งเบิกที่คำนวณแล้ว"
// shows doc_2+doc_5 (regular cert) or doc_3+doc_4 (international cert);
// "ตารางเรียนตารางสอน" shows doc_7. doc_6 rides only inside the merged PDF /
// department ZIP — it never gets its own row on either page (decision 3).
export const DISBURSEMENT_DOC_LABELS: Record<string, string> = {
  doc_2: 'ตารางการคำนวณค่าสอนพิเศษเกินภาระงานสอน (ภาคปกติ)',
  doc_5: 'หลักฐานการเบิกจ่ายเงินค่าสอนพิเศษ (ภาคปกติ)',
  doc_3: 'รายงานภาระการสอน (ภาคนานาชาติ)',
  doc_4: 'หลักฐานการจ่ายเงินค่าตอบแทนการสอน (ภาคนานาชาติ)',
  doc_7: 'ตารางเรียนตารางสอน',
  doc_6: 'ใบปะหน้า/บันทึกข้อความ',
  merged: 'เอกสารรวมพร้อมส่งเบิก',
};

// The "เอกสารส่งเบิกที่คำนวณแล้ว" doc_type list for a cert's own path — never
// mixes in the other path's docs (decision 8: a cert's sections show only its
// own path's docs).
export const DISBURSEMENT_CALC_DOC_TYPES: Record<DisbursementPath, DisbursementDocType[]> = {
  regular: ['doc_2', 'doc_5'],
  international: ['doc_3', 'doc_4'],
};

export const DISBURSEMENT_SCHEDULE_DOC_TYPES: DisbursementDocType[] = ['doc_7'];

export interface DisbursementRevisionAction {
  action_code?: string | null;
  actor_role_code?: string | null;
  comment?: string | null;
  acted_at?: string | null;
  actor?: {
    id?: number | null;
    full_name?: string | null;
    email?: string | null;
  } | null;
}

// Full `ApprovalRequest#as_json` — kept intentionally loose beyond the fields
// the UI reads (`active_blockers` is always empty for this workflow per §4).
export interface DisbursementApprovalRequest {
  id?: number;
  status_code?: string | null;
  current_status?: unknown;
  active_blockers?: unknown[];
  latest_revision_action?: DisbursementRevisionAction | null;
}

// v1.2 §3 — Disbursement::StationResolver's per-station entry, exposed as
// `approval_track` on both show and index. `state` drives the stepper's
// done/current/waiting styling; `acted_at` is only populated for `done`.
export type DisbursementApprovalTrackState = 'done' | 'current' | 'waiting';

export interface DisbursementApprovalTrackEntry {
  station: string;
  role_label: string;
  person_names: string[];
  state: DisbursementApprovalTrackState;
  acted_at?: string | null;
}

export interface DisbursementCertificationModel {
  id: number;
  locked_at?: string | null;
  revision_comment?: string | null;
  // Set by RequestRevisionService on send-back, cleared by
  // RevisionReviewService once รองวิชาการ resolves it — the pending station
  // the cert will resume at (approve) or advance past (reject).
  revision_requested_from_status?: DisbursementStatusCode | null;
  compensation_snapshot?: DisbursementCompensationSnapshot | null;
  active_bundle_document_id?: number | null;
  // Set once a weekly DisbursementBundleDeliveryCycleJob round marks this
  // cert `delivered`.
  delivered_at?: string | null;
  teacher_notified_at?: string | null;
  head_notified_at?: string | null;
  academic_term: DisbursementAcademicTerm;
  user: DisbursementUser;
  department: DisbursementDepartment;
  // v1.2 decision 8 — DisbursementCertification#path/#path_label.
  path: DisbursementPath;
  path_label: string;
  status_code: DisbursementStatusCode;
  status_label: string;
  teacher_revision_allowed: boolean;
  teacher_revision_deadline?: string | null;
  head_revision_allowed: boolean;
  head_revision_deadline?: string | null;
  documents: DisbursementDocumentEntry[];
  approval_request?: DisbursementApprovalRequest | null;
  approval_track: DisbursementApprovalTrackEntry[];
  // Not enumerated in the model's #as_json but referenced by the default
  // sort (`sort ... default updated_at`) — standard AR timestamps are
  // assumed present; treated as optional/best-effort for the "updated" column.
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DisbursementCertificationListParams {
  academic_term_id?: number | string | null;
  status?: DisbursementStatusCode | null;
  scope?: 'completed' | null;
  department_id?: number | string | null;
  keywords?: string | null;
  page?: number;
  limit?: number;
  sort?: 'status' | 'updated_at' | null;
  direction?: 'asc' | 'desc' | null;
}

export interface DisbursementCertificationListResponse {
  data: DisbursementCertificationModel[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// Generalized send-back — serves teacher/head/finance/vice_dean_administration
// alike (the backend dispatches on the caller's active role + the cert's
// current station). Always reason-only in v1.1+ — no typed revision_type.
export interface DisbursementRequestRevisionPayload {
  comment: string;
}

// v1.2 decision 6 — dev-only "เปิดรอบเบิกจ่าย (ทดสอบ)" trigger, relocated from
// the deleted settings/disbursement page onto ข้อมูลเอกสารส่งเบิก, gated on
// environment.enableDisbursementTestTrigger. Calls
// Disbursement::ProvisionService.call(force: true) for the given term.
export interface DisbursementTestProvisionResponse {
  triggered: boolean;
  certifications_count: number;
}

// §7 bulk_head_certify — per-id independent outcomes (one failure doesn't
// roll back the others).
export interface DisbursementBulkResultItem {
  certification_id: number;
  ok: boolean;
  certification?: DisbursementCertificationModel;
  error?: string;
}

export interface DisbursementBulkHeadCertifyResponse {
  results: DisbursementBulkResultItem[];
}
